import React, {useEffect} from "react"
import {useQueryState} from "use-location-state"
import {upperFirst, startCase} from "lodash"
import ManagedSolutionsTransactionsTable from "./ManagedSolutionsTransactionsTable"
import {usePaging} from "../../hooks/usePaging"
import {useAccessToken} from "../../services/auth"
import {findManagedSolutionsTransactions, exportManagedSolutionsTransactions} from "../../resources/managedSolutions"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {DataTableCard, DataTableActionHeader, TablePending} from "../DataTable/DataTable"
import {downloadFile} from "../../utilities/file"
import {useToasts} from "react-toast-notifications"
import SearchBox from "../SearchBox/SearchBox"
import DatePickerWithPresets, {DatePickerPresetKeys} from "../DatePicker/DatePicker"
import {Filter} from "../Filter/Filter"
import {CurrencyRangeFilter} from "../Filter/CurrencyRangeFilter"
import {Cents, TransactionTypes} from "../../resources/transaction"

interface ManagedSolutionsTransactionsProps {
	customerId?: string
	accountId?: string
	fullHeight?: boolean
	limit?: number
	isUsingPaging?: boolean
	enableTitle?: boolean
	enableExport?: boolean
	enableSearch?: boolean
	enableDateFilter?: boolean
	enableAmountFilter?: boolean
	enableTypeFilter?: boolean
	title?: string
}

export default function ManagedSolutionsTransactions({
	customerId,
	accountId,
	fullHeight = false,
	limit = 10,
	isUsingPaging = true,
	enableTitle = true,
	enableExport = true,
	enableSearch = true,
	enableDateFilter = true,
	enableAmountFilter = true,
	enableTypeFilter = true,
	title = "Transactions",
}: ManagedSolutionsTransactionsProps) {
	const prefix = "tx-"
	const accessToken = useAccessToken()
	const {addToast} = useToasts()
	const [query, setQuery] = useQueryState("transations-search", "")
	const [searchBox, setIsSearchLoading] = SearchBox(query, "Search Transactions", 500, onSearch)
	const [sortBy, setSortBy] = useQueryState(`${prefix}-sort`, "-createdAt")
	const [since, setSince] = useQueryState(`${prefix}-filter[since]`, "")
	const [until, setUntil] = useQueryState(`${prefix}-filter[until]`, "")
	const [filteredTransactions, setFilteredTransactions] = useQueryState<string[]>(`${prefix}-filter[type]`, [])
	const [fromAmount, setFromAmount] = useQueryState<Cents | "">(`${prefix}-filter[fromAmount]`, "")
	const [toAmount, setToAmount] = useQueryState<Cents | "">(`${prefix}-filter[toAmount]`, "")

	const transactionTypesMap = new Map(
		Object.entries(TransactionTypes)
			.map((t) => [upperFirst(t[1].replace("Transaction", "")), startCase(t[0])] as [string, string])
			.sort((a, b) => a[1].localeCompare(b[1]))
	)

	const [result, hasPrev, hasNext, prev, next, hasResults, reset] = usePaging(
		limit,
		(offset, limit) =>
			findManagedSolutionsTransactions({
				accessToken,
				offset,
				limit,
				customerId,
				accountId,
				searchQuery: enableSearch ? query : "",
				sort: sortBy,
				since,
				until,
				types: filteredTransactions,
				fromAmount,
				toAmount,
			}),
		(x) => x.data.length,
		[customerId, accountId, query, sortBy, since, until, [...filteredTransactions].join(","), fromAmount, toAmount],
		prefix,
		true
	)

	useEffect(() => {
		let isActive = true
		if (isActive) {
			result.match(
				() => null,
				() => setIsSearchLoading(false),
				() => setIsSearchLoading(false)
			)
		}
		return () => {
			isActive = false
		}
	}, [result])

	function onSearch(searchTerm: string) {
		setQuery(searchTerm)
		reset(limit)
	}

	function toggleSort() {
		setSortBy(sortBy === "-createdAt" ? "createdAt" : "-createdAt")
		reset(limit)
	}

	async function exportTransactions() {
		const response = await exportManagedSolutionsTransactions(
			accessToken,
			customerId,
			accountId,
			since,
			until,
			filteredTransactions,
			fromAmount,
			toAmount
		)

		response.match(
			(d) => downloadFile("ManagedSolutionsTransactions.csv", d, "text/csv"),
			() => {
				addToast("Something went wrong", {appearance: "warning"})
			}
		)

		return true
	}

	const dateTime = (
		<DatePickerWithPresets
			presets={[
				DatePickerPresetKeys.allTime,
				DatePickerPresetKeys.yesterday,
				DatePickerPresetKeys.lastMonth,
				DatePickerPresetKeys.last3Months,
				DatePickerPresetKeys.last6Months,
				DatePickerPresetKeys.last30Days,
				DatePickerPresetKeys.last7Days,
				DatePickerPresetKeys.custom,
			]}
			onDateChanged={(s, u) => {
				setSince(s)
				setUntil(u)
				reset(limit)
			}}
		/>
	)

	const typeFilter = (
		<Filter
			onFilterFunc={() => reset(limit)}
			options={transactionTypesMap}
			setStatuses={setFilteredTransactions}
			statuses={filteredTransactions}
			title="All types"
		/>
	)

	const amountFilter = (
		<CurrencyRangeFilter
			placeholder={"Amount"}
			presets={[]}
			from={fromAmount}
			setFrom={(value) => {
				setFromAmount(value)
				reset(limit)
			}}
			to={toAmount}
			setTo={(value) => {
				setToAmount(value)
				reset(limit)
			}}
		/>
	)

	return (
		<DataTableCard className={"managed-solutions-transactions-card"}>
			<DataTableActionHeader
				enableSticky={fullHeight}
				title={enableTitle ? title : null}
				searchBox={enableSearch ? searchBox : null}
				dateTimePicker={enableDateFilter ? dateTime : null}
				filters={[...(enableTypeFilter ? [typeFilter] : []), ...(enableAmountFilter ? [amountFilter] : [])]}
				exportFunc={enableExport ? exportTransactions : undefined}
			/>
			<AsyncResultComponent asyncResult={result} pendingComponent={<TablePending numberOfRows={6} />}>
				{({value: transactions}) => {
					return (
						<ManagedSolutionsTransactionsTable
							transactions={transactions.data}
							hasResults={hasResults}
							hasPrev={hasPrev}
							hasNext={hasNext}
							prev={prev}
							next={next}
							isUsingPaging={isUsingPaging}
							fullHeight={fullHeight}
							meta={transactions.meta}
							sortFunction={toggleSort}
							sortBy={sortBy}
						/>
					)
				}}
			</AsyncResultComponent>
		</DataTableCard>
	)
}
